// Persisted map view: camera + filters survive reloads and navigation away to
// an entity page and back.
//
// The URL stays the source of truth — a shared link must render exactly what
// the sender saw. localStorage only fills in what the URL omits, and camera and
// filters restore independently: a link carrying just a camera (`?lng=…&lat=…`)
// must not pick up the recipient's saved filters, and vice versa.
import { EMPTY_FILTERS, filtersToParams, type FilterState } from "../lib/filters";

const STORAGE_KEY = "gaea.map.v1";

export type StoredCamera = { lng: number; lat: number; zoom: number };
type Stored = { camera?: StoredCamera; filters?: FilterState };

/** URL params that mean "this link specifies a camera". */
const CAMERA_PARAMS = ["lng", "lat", "z"] as const;

/**
 * URL params that mean "this link specifies filters" — the keys
 * `filtersToParams` writes. Layer toggles count: `?tracks=1` alone is a
 * deliberate view worth honouring as-is.
 */
const FILTER_PARAMS = ["act", "pt", "from", "to", "emin", "emax", "dmin", "dmax", "amin", "q", "routes", "tracks", "regions", "outings", "events", "cluster"] as const;

function read(): Stored {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    return {};
  }
}

function write(next: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode or a full quota — persistence is a convenience, never fatal.
  }
}

export function saveCamera(camera: StoredCamera): void {
  if (!Number.isFinite(camera.lng) || !Number.isFinite(camera.lat) || !Number.isFinite(camera.zoom)) return;
  write({ ...read(), camera });
}

export function saveFilters(filters: FilterState): void {
  write({ ...read(), filters });
}

export function loadCamera(): StoredCamera | null {
  const { camera } = read();
  if (!camera) return null;
  const { lng, lat, zoom } = camera;
  if (![lng, lat, zoom].every((n) => typeof n === "number" && Number.isFinite(n))) return null;
  // Guard against a corrupted entry parking the map off-world.
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180 || zoom < 0 || zoom > 24) return null;
  return { lng, lat, zoom };
}

/**
 * Stored filters, normalized against EMPTY_FILTERS so entries written by an
 * older build (missing keys added since) can't produce an undefined toggle.
 */
export function loadFilters(): FilterState | null {
  const { filters } = read();
  if (!filters || typeof filters !== "object") return null;
  return {
    ...EMPTY_FILTERS,
    ...filters,
    activities: Array.isArray(filters.activities) ? filters.activities : [],
    placeTypes: Array.isArray(filters.placeTypes) ? filters.placeTypes : [],
  };
}

const hasAny = (params: URLSearchParams, keys: readonly string[]) => keys.some((k) => params.has(k));

export const urlHasCamera = (params: URLSearchParams) => hasAny(params, CAMERA_PARAMS);
export const urlHasFilters = (params: URLSearchParams) => hasAny(params, FILTER_PARAMS);

/**
 * Params the map should start from: whatever the URL already specifies, plus
 * saved filters when it specifies none. Returns null when there is nothing to
 * restore, so the caller can skip a pointless history replace.
 *
 * The camera is deliberately not written here — MapView takes it as
 * `initialCamera` and echoes it back through onCameraChange on first render,
 * which puts it in the URL without a second replace.
 */
export function restoreParams(params: URLSearchParams): URLSearchParams | null {
  if (urlHasFilters(params)) return null;
  const filters = loadFilters();
  if (!filters) return null;
  const next = new URLSearchParams(params);
  filtersToParams(filters, next);
  return next;
}
